import React from 'react';
import {
  createOneShotActionHandlers,
  oneShotEditQuoteReducer,
  type OneShotEditQuoteHandlers,
} from './reducer/oneShotEditQuoteReducer';
import type { OneShotEditQuoteState } from './types';
import type { GetPrimeXQuoteDetailsResponse } from '../../../../../../../api/quotes.api.types';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

type OneShotEditQuoteContextValue = {
  state: OneShotEditQuoteState;
  handlers: OneShotEditQuoteHandlers;
  quoteDetails: UseQueryResult<GetPrimeXQuoteDetailsResponse, AxiosError> | null;
};

const OneShotEditQuoteContext =
  React.createContext<OneShotEditQuoteContextValue | null>(null);

type OneShotEditQuoteProviderProps = {
  initialState: OneShotEditQuoteState;
  quoteDetails: UseQueryResult<GetPrimeXQuoteDetailsResponse, AxiosError> | null;
  children: React.ReactNode;
};

export const OneShotEditQuoteProvider = ({
  initialState,
  quoteDetails,
  children,
}: OneShotEditQuoteProviderProps) => {
  const [state, dispatch] = React.useReducer(
    oneShotEditQuoteReducer,
    initialState
  );

  const handlers = React.useMemo(
    () => createOneShotActionHandlers(dispatch),
    []
  );

  const value = React.useMemo(
    () => ({
      state,
      handlers,
      quoteDetails,
    }),
    [state, handlers, quoteDetails]
  );

  return (
    <OneShotEditQuoteContext.Provider value={value}>
      {children}
    </OneShotEditQuoteContext.Provider>
  );
};

export const useOneShotEditQuoteContext = () => {
  const context = React.useContext(OneShotEditQuoteContext);

  if (!context) {
    throw new Error(
      'useOneShotEditQuoteContext must be used within OneShotEditQuoteProvider'
    );
  }

  return context;
};

export const useOneShotEditQuoteContextSafe = () => {
  return React.useContext(OneShotEditQuoteContext);
};
